import React from 'react';
import { Check, ChevronDown, ExternalLink, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { channelApi } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const field = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400';
const label = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500';

function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function normalizeAdminStatus(user) {
  return String(user?.admin_status || user?.adminStatus || user?.role || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

function memberLabelFromCode(code) {
  const key = String(code || '').trim().toLowerCase();
  const map = {
    inj: 'InJourney Group',
    injourney: 'InJourney Group',
    internal: 'Internal PRIME',
    ias: 'IAS / Airports',
    airport: 'IAS / Airports',
    airports: 'IAS / Airports',
    aviation: 'Aviation Services',
    aviasi: 'Aviation Services',
    hospitality: 'Hospitality',
    hin: 'Hospitality',
    idm: 'Destination Management',
    twc: 'Destination Management / TWC',
    tmii: 'Destination Management / TMII',
    retail: 'Retail',
    sarinah: 'Retail / Sarinah',
    srn: 'Retail / Sarinah',
    itdc: 'Tourism Development / ITDC',
    tourism: 'Tourism Development',
  };

  if (!key) return 'Unassigned Member';
  return map[key] || key.toUpperCase();
}

function itemKey(item) {
  return [
    item.type || 'catalog',
    item.packageCode || item.productCode || item.code || '',
    item.source || 'source',
    item.packageId || item.productId || '',
  ].join('::');
}

function itemCode(item) {
  return item.packageCode || item.productCode || item.code || '';
}

function itemName(item) {
  return item.packageName || item.productName || item.name || itemCode(item) || '-';
}

function itemBadge(item) {
  return item.type === 'package' ? 'Package' : 'Single Product';
}

function itemSearchText(item) {
  return [
    itemName(item),
    itemCode(item),
    item.memberCode,
    item.member?.code,
    memberLabelFromCode(item.memberCode),
    item.memberName,
    item.member?.name,
    item.source,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}


function CatalogCombobox({ groups, value, onChange, placeholder, loading, typeLabel }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const allItems = React.useMemo(() => groups.flatMap((group) => group.items), [groups]);
  const selected = allItems.find((item) => itemKey(item) === value) || null;
  const needle = query.trim().toLowerCase();

  const visibleGroups = React.useMemo(() => {
    if (!needle) return groups;
    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => itemSearchText(item).includes(needle)),
      }))
      .filter((group) => group.items.length > 0);
  }, [groups, needle]);

  React.useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  return (
    <div>
      <label className={label}>Pilih dari Catalog PRIME</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`${field} flex min-h-10 items-center justify-between gap-3 text-left`}
            aria-expanded={open}
          >
            <span className={`min-w-0 flex-1 truncate ${selected ? 'text-slate-900' : 'text-slate-500'}`}>
              {selected ? `${itemName(selected)} [${itemCode(selected)}]` : placeholder}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] min-w-[320px] max-w-[680px] p-0"
        >
          <div className="border-b border-slate-200 p-2.5">
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3">
              <Search className="h-4 w-4 shrink-0 text-slate-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cari nama product, code, atau member..."
                className="h-10 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setOpen(false);
                }}
              />
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto p-1.5">
            {loading ? (
              <div className="px-3 py-6 text-center text-sm text-slate-500">Memuat catalog...</div>
            ) : visibleGroups.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-slate-500">Catalog tidak ditemukan.</div>
            ) : (
              visibleGroups.map((group) => (
                <div key={group.key} className="mb-2 last:mb-0">
                  <div className="sticky top-0 z-10 bg-slate-50 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    {group.label} ({group.items.length})
                  </div>
                  <div className="py-1">
                    {group.items.map((item) => {
                      const key = itemKey(item);
                      const active = key === value;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => {
                            onChange(key);
                            setOpen(false);
                          }}
                          className={`flex w-full items-start gap-2 rounded-md px-3 py-2 text-left text-sm transition hover:bg-slate-100 ${active ? 'bg-slate-100 font-semibold text-slate-900' : 'text-slate-700'}`}
                        >
                          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                            {active ? <Check className="h-4 w-4" /> : null}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate">{itemName(item)}</span>
                            <span className="mt-0.5 block truncate text-xs font-normal text-slate-500">
                              {itemCode(item)} · {item.memberName || memberLabelFromCode(item.memberCode)}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="border-t border-slate-200 px-3 py-2 text-xs text-slate-500">
            {allItems.length} item {typeLabel} · Search tersedia di dalam select
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function PurchaseSimulator() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const adminStatus = normalizeAdminStatus(user);
  const allowed = adminStatus === 'admin_inj';

  const [catalog, setCatalog] = React.useState([]);
  const [channels, setChannels] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [initialLoading, setInitialLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');
  const [emailNotice, setEmailNotice] = React.useState('');
  const [selectedCatalogKey, setSelectedCatalogKey] = React.useState('');
  const [form, setForm] = React.useState({
    kind: 'package',
    code: '',
    displayName: '',
    memberCode: '',
    travelDate: new Date().toISOString().slice(0, 10),
    name: 'Demo Customer',
    email: '',
    phone: '',
    channelClientId: '',
    dryRun: true,
  });

  const load = React.useCallback(async () => {
    if (!allowed) return;
    setInitialLoading(true);
    setError('');

    const [catalogResult, channelsResult] = await Promise.allSettled([
      channelApi.get('/admin/simulator/catalog?limit=300'),
      channelApi.get('/admin/simulator/channels'),
    ]);

    if (catalogResult.status === 'fulfilled') {
      setCatalog(Array.isArray(catalogResult.value.data?.data) ? catalogResult.value.data.data : []);
    } else {
      setCatalog([]);
      const e = catalogResult.reason;
      setError(e.response?.data?.message || e.message || 'Gagal membaca katalog PRIME lokal.');
    }

    if (channelsResult.status === 'fulfilled') {
      setChannels(Array.isArray(channelsResult.value.data?.data) ? channelsResult.value.data.data : []);
    } else {
      setChannels([]);
      console.warn('PRIME simulator channels unavailable:', channelsResult.reason);
    }

    setInitialLoading(false);
  }, [allowed]);

  React.useEffect(() => {
    load();
  }, [load]);

  const filteredCatalog = React.useMemo(
    () => catalog.filter((item) => (item.type || 'product') === form.kind),
    [catalog, form.kind],
  );

  const groupedCatalog = React.useMemo(() => {
    const groups = new Map();
    filteredCatalog.forEach((item) => {
      const memberKey = String(item.memberCode || '').trim().toLowerCase() || 'unassigned';
      const memberLabel = item.memberName || memberLabelFromCode(item.memberCode);
      if (!groups.has(memberKey)) {
        groups.set(memberKey, {
          key: memberKey,
          label: memberLabel,
          items: [],
        });
      }
      groups.get(memberKey).items.push(item);
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        items: group.items.sort((a, b) => itemName(a).localeCompare(itemName(b))),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredCatalog]);

  React.useEffect(() => {
    if (!selectedCatalogKey) return;
    const exists = filteredCatalog.some((item) => itemKey(item) === selectedCatalogKey);
    if (!exists) {
      setSelectedCatalogKey('');
      setForm((prev) => ({
        ...prev,
        code: '',
        displayName: '',
        memberCode: '',
      }));
    }
  }, [filteredCatalog, selectedCatalogKey]);

  if (!allowed) {
    return (
      <div className="mx-auto max-w-3xl rounded-xl border border-red-100 bg-white p-8">
        <h1 className="text-xl font-bold">Akses dibatasi</h1>
        <p className="mt-2 text-slate-600">Purchase Simulator hanya tersedia untuk Admin INJ.</p>
      </div>
    );
  }

  const chooseCatalog = (event) => {
    const value = event.target.value;
    setSelectedCatalogKey(value);
    setError('');
    setEmailNotice('');

    if (!value) {
      setForm((prev) => ({
        ...prev,
        code: '',
        displayName: '',
        memberCode: '',
      }));
      return;
    }

    const item = filteredCatalog.find((entry) => itemKey(entry) === value);
    if (!item) return;

    setForm((prev) => ({
      ...prev,
      kind: item.type === 'product' ? 'product' : 'package',
      code: itemCode(item),
      displayName: itemName(item),
      memberCode: item.member?.code || item.memberCode || '',
    }));
  };

  const execute = async () => {
    setLoading(true);
    setError('');
    setEmailNotice('');
    setResult(null);

    try {
      const payload = {
        travel_date: form.travelDate,
        passengers: [{ name: form.name, gender: 'Laki-laki' }],
        contact_details: { email: form.email, phone: form.phone },
        dry_run: form.dryRun,
        channel_client_id: form.channelClientId || undefined,
        display_name: form.displayName,
        member_code: form.memberCode || undefined,
      };

      payload[form.kind === 'product' ? 'product_code' : 'package_code'] = form.code;
      const response = await channelApi.post('/admin/simulator/purchase', payload, { timeout: 60000 });
      setResult(response.data?.data || response.data);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const sendDummyEmail = async () => {
    if (!form.email) {
      setError('Isi Customer Email terlebih dahulu.');
      return;
    }

    setLoading(true);
    setError('');
    setEmailNotice('');

    try {
      const response = await channelApi.post('/admin/simulator/test-email', {
        email: form.email,
        name: form.name,
        travel_date: form.travelDate,
        display_name: form.displayName,
        member_code: form.memberCode,
      });

      setEmailNotice(response.data?.message || `Dummy e-ticket berhasil dikirim ke ${form.email}`);
    } catch (e) {
      setError('Gagal mengirim dummy e-ticket: ' + (e.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  };

  const preview = async (type) => {
    if (!result) return;
    setError('');

    try {
      if (!result.simulation && result.bookingId) {
        const response = await channelApi.get(`/admin/simulator/booking/${result.bookingId}/ticket.${type}`, {
          responseType: 'blob',
        });

        if (type === 'html') {
          window.open(URL.createObjectURL(response.data), '_blank', 'noopener');
        } else {
          saveBlob(response.data, `PRIME-${result.bookingId}.pdf`);
        }
        return;
      }

      const booking = {
        id: result.id || result.bookingId,
        invoiceCode: result.invoiceCode,
        packageName: result.packageName || form.displayName,
        packageCode: form.code,
        travelDate: form.travelDate,
        paymentStatus: result.paymentStatus || 'SIMULATED',
        passengers: [{ name: form.name }],
      };

      const response = await channelApi.post(`/admin/simulator/ticket-preview.${type}`, {
        booking,
        vouchers: result.vouchers || result.supplier_notification_status || [],
      }, {
        responseType: 'blob',
      });

      if (type === 'html') {
        window.open(URL.createObjectURL(response.data), '_blank', 'noopener');
      } else {
        saveBlob(response.data, 'PRIME-Simulation.pdf');
      }
    } catch (e) {
      setError('Gagal membuat preview ticket: ' + (e.response?.data?.message || e.message));
    }
  };

  const selectedCatalogItem = filteredCatalog.find((item) => itemKey(item) === selectedCatalogKey) || null;
  const totalFiltered = filteredCatalog.length;

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[.18em] text-slate-500">Internal Integration Tools</div>
        <h1 className="mt-1 text-2xl font-bold text-slate-900">PRIME Purchase Simulator</h1>
        <p className="mt-1 text-sm text-slate-600">Simulasikan alur Channel → Purchase → Supplier → Credential → E-Ticket tanpa membuat marketplace baru.</p>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1.1fr_.9fr]">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="font-bold">Purchase Request</h2>
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${form.dryRun ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
              {form.dryRun ? 'DRY RUN' : 'LIVE EXECUTE'}
            </span>
          </div>

          <div className="space-y-4">
            <div>
              <label className={label}>Type</label>
              <select
                className={field}
                value={form.kind}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    kind: e.target.value,
                    code: '',
                    displayName: '',
                    memberCode: '',
                  }));
                  setSelectedCatalogKey('');
                }}
              >
                <option value="package">Package</option>
                <option value="product">Single Product</option>
              </select>
            </div>

            <CatalogCombobox
              groups={groupedCatalog}
              value={selectedCatalogKey}
              onChange={(value) => chooseCatalog({ target: { value } })}
              placeholder={`— Pilih ${form.kind === 'package' ? 'package' : 'single product'} —`}
              loading={initialLoading}
              typeLabel={form.kind === 'package' ? 'package' : 'single product'}
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className={label}>Code</label>
                <input className={field} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
              </div>
              <div>
                <label className={label}>Member</label>
                <input className={field} value={selectedCatalogItem?.member?.name || selectedCatalogItem?.memberName || memberLabelFromCode(form.memberCode)} readOnly />
              </div>
            </div>

            {selectedCatalogItem?.member?.code && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-center">
                  <div className="grid gap-1 text-slate-700 sm:grid-cols-3">
                    <div><span className="text-xs font-semibold uppercase text-slate-500">Member</span><br />{selectedCatalogItem.member.code}</div>
                    <div><span className="text-xs font-semibold uppercase text-slate-500">Integration</span><br />{selectedCatalogItem.integration?.status || 'Not available'}</div>
                    <div><span className="text-xs font-semibold uppercase text-slate-500">Transport</span><br />{selectedCatalogItem.integration?.transport || 'Not available'}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/internal-tools/api-documentation/${selectedCatalogItem.member.code}?product=${encodeURIComponent(itemCode(selectedCatalogItem))}`)}
                    className="inline-flex items-center justify-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    View Integration
                  </button>
                </div>
              </div>
            )}

            <div>
              <label className={label}>Display Name</label>
              <input className={field} value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className={label}>Travel / Visit Date</label>
                <input type="date" className={field} value={form.travelDate} onChange={(e) => setForm({ ...form, travelDate: e.target.value })} />
              </div>
              <div>
                <label className={label}>Passenger</label>
                <input className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className={label}>Customer Email</label>
                <input type="email" className={field} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="customer@example.com" />
              </div>
              <div>
                <label className={label}>Phone</label>
                <input className={field} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+62..." />
              </div>
            </div>

            {!form.dryRun && (
              <div>
                <label className={label}>Execution Channel</label>
                <select className={field} value={form.channelClientId} onChange={(e) => setForm({ ...form, channelClientId: e.target.value })}>
                  <option value="">— Pilih registered channel —</option>
                  {channels.map((channel) => (
                    <option key={channel.client_id} value={channel.client_id}>
                      {channel.channel_name} {channel.env ? `(${channel.env})` : ''}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-red-600">Live Execute menggunakan purchase flow asli: dapat mengurangi quota dan memanggil supplier API.</p>
              </div>
            )}

            <label className="flex cursor-pointer gap-3 rounded-xl bg-slate-50 p-3 text-sm">
              <input type="checkbox" checked={form.dryRun} onChange={(e) => setForm({ ...form, dryRun: e.target.checked })} />
              <span>
                <b>Dry Run (recommended)</b>
                <br />
                <span className="text-slate-500">Tidak menulis booking, tidak mengurangi quota, tidak call supplier, tidak kirim email.</span>
              </span>
            </label>

            <button
              disabled={loading || !form.code || !form.travelDate || !form.email || (!form.dryRun && !form.channelClientId)}
              onClick={execute}
              className="w-full rounded-xl bg-[#12284A] px-4 py-3 font-semibold text-white disabled:opacity-40"
            >
              {loading ? 'Processing…' : form.dryRun ? 'Run Simulation' : 'Execute Real Purchase Flow'}
            </button>

            <button
              disabled={loading || !form.email}
              onClick={sendDummyEmail}
              className="w-full rounded-xl border border-[#12284A] bg-white px-4 py-3 font-semibold text-[#12284A] disabled:opacity-40"
            >
              Kirim Dummy E-Ticket ke Email
            </button>

            <p className="text-xs text-slate-500">Untuk test tampilan email tanpa purchase/supplier call. Pastikan SMTP local sudah dikonfigurasi.</p>

            {emailNotice && <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">{emailNotice}</div>}
            {error && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700 whitespace-pre-wrap">{error}</div>}
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="font-bold">Result & Ticket</h2>

          {!result ? (
            <div className="mt-5 rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">
              Hasil booking, credential, dan preview e-ticket akan muncul di sini.
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl bg-slate-50 p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Booking ID</div>
                  <div className="mt-1 font-bold text-slate-900">{result.id || result.bookingId || '-'}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Invoice</div>
                  <div className="mt-1 font-bold text-slate-900">{result.invoiceCode || '-'}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Status</div>
                  <div className="mt-1 font-bold text-slate-900">{result.paymentStatus || result.status || (form.dryRun ? 'SIMULATED' : 'Processed')}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Product Type</div>
                  <div className="mt-1 font-bold text-slate-900">{itemBadge({ type: form.kind })}</div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Credentials</div>
                    <div className="text-xs text-slate-500">Preview credential hasil simulator atau purchase flow nyata.</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => preview('html')} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">Preview HTML</button>
                    <button onClick={() => preview('pdf')} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">Download PDF</button>
                  </div>
                </div>

                <div className="space-y-3">
                  {(result.vouchers || result.supplier_notification_status || []).length === 0 ? (
                    <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">Belum ada credential yang dikembalikan.</div>
                  ) : (
                    (result.vouchers || result.supplier_notification_status || []).map((voucher, index) => (
                      <div key={`${voucher.voucherCode || voucher.code || index}`} className="rounded-xl border border-slate-200 p-4">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700">{memberLabelFromCode(voucher.memberCode || form.memberCode)}</span>
                          <span className="text-xs text-slate-500">{voucher.status || 'Pending'}</span>
                        </div>
                        <div className="font-semibold text-slate-900">{voucher.componentName || voucher.name || form.displayName || '-'}</div>
                        <div className="mt-2 grid gap-2 text-sm text-slate-600">
                          <div><span className="font-semibold text-slate-700">Code:</span> {voucher.voucherCode || voucher.qrData || '-'}</div>
                          <div><span className="font-semibold text-slate-700">Supplier:</span> {voucher.supplierName || voucher.memberCode || 'InJourney Member'}</div>
                          <div><span className="font-semibold text-slate-700">Valid Until:</span> {voucher.validUntil || form.travelDate}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
